import * as React from 'react';
import { ProtectedPageWrapper } from '../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { SingleColumnLayout } from '../designSystem/layouts/SingleColumnLayout';
import { H2 } from '../designSystem/typography/Heading';
import { LoadingPageOverlay } from '../designSystem/LoadingPageOverlay';
import { Flex, FlexItem } from '../designSystem/compositeComponents/FlexHelpers';
import { createLocation, deleteLocation, getFacilityTypes, getLocations } from '../api/locationClientApi';
import { FacilityType, Location, rng, stringUtil } from '../amorg-common';
import { FluidLeftFixedRightLayoutColumns } from '../designSystem/layouts/FluidLeftFixedRightLayout';
import { Card } from '../designSystem/Card';
import { Small } from '../designSystem/typography/Body';
import { useEffect } from 'react';
import FacilityList from '../features/locations/FacilityList';
import { AmorgMenuButton } from '../designSystem/amorgMenu/AmorgMenuButton';
import { LocationEditor } from '../features/locations/LocationEditor';
import { Button } from '../designSystem/button/Button';
import PersonList from '../features/locations/PersonList';
import { Modal } from '../designSystem/Modal';
import { MessageCard } from '../designSystem/Message';
import { PageHeader } from '../designSystem/PageHeader';



type State = {
	loading:boolean,
	locations:Location[],
    allFacilityTypes:FacilityType[]
}

export default function LocationListingPage(): JSX.Element {
    
    const [inEdit, setInEdit] = React.useState<Location>(null);
    const [state, setState] = React.useState<State>({
        loading:true,
        locations:null,
        allFacilityTypes:null
    });


    useEffect(() => {
        if(state.locations === null)
            getLocationsList();
    }, [])

    
    async function onLocationClose(){
        setInEdit(null);
        getLocationsList()
    }

    async function getLocationsList() {
        const locations = await getLocations();
        const allFacilityTypes = await getFacilityTypes();
        setState({locations, allFacilityTypes, loading:false});
    }

    async function handleCreateLocation(){
        setState({
            ...state,
            loading:true
        });
        const allLocations = await getLocations();
        
        const id = rng.getRandomId('loc_');

        const nameBase = 'Ny lokation';
        const nextName = stringUtil.generateNextSequenceName(nameBase, allLocations.map(location => location.name));

        const createResult = await createLocation(id, nextName, ``, ``, ``, ``);
        if (createResult.isSuccess) {
            const locations = await getLocations();
            setState({...state, locations, loading:false});
            setInEdit(locations.find(x => x.id === id));
        }
    }


    function handleLocationSelected(locationId:string){
        const location = state.locations.find( x => x.id === locationId)
        setInEdit(location)
    }

    async function handleLocationDeleted() {
        setState({...state, loading:true});
        const locations = await getLocations();
        setState({...state, locations, loading:false});
    }

    async function handleLocationUpdated(){
        setState({ ...state, loading:true, })
        const locations = await getLocations();
        setState({...state, locations, loading:false});
        if(inEdit){
            setInEdit(locations.find(x => x.id === inEdit.id));
        }
    }

    return (
        <ProtectedPageWrapper requiredPermissions={['ViewLocations']} selectedMenuItemKey='locations'>
            <SingleColumnLayout
                noDivider
                contentContainer={
                    <>
                        <LoadingPageOverlay loading={state.loading} />

                        <PageHeader 
                            headerIcon='navigation_home'
                            headerText="Lokationer" 
                            headerStyle='secondary-gray'
                            buttons={
                                <Button variant="filled" onClick={handleCreateLocation} text="Opret ny lokation" icon="actions_plus" />
                            } 
                        />

                        <FluidLeftFixedRightLayoutColumns
                            leftContainer={
                                <>
                                <Flex flexDirection="column" gap={24}>
                                    {(!state.locations || state.locations?.length === 0) && 
                                    <>
                                        <MessageCard variant='info'>
                                            Ingen lokationer oprettet
                                        </MessageCard>
                                    </>
                                    }
                                    
                                    {state.locations && state.locations.map(l => {
                                        return (
                                            <LocationView 
                                                location={l}
                                                allFacilityTypes={state.allFacilityTypes}
                                                key={l.id} 
                                                onLocationDeleted={handleLocationDeleted} 
                                                onLocationSelected={() => handleLocationSelected(l.id)}
                                                onLocationUpdated={handleLocationUpdated}
                                            />
                                        );
                                    })}
                                    
                                </Flex>
                                </>
                            }
                            rightContainer={
                                
                                <PersonList />
                                
                            } 
                        />
                    </>
                }
            />
            
            <Modal 
                title={"Opret lokation"}
                content={
                    <LocationEditor 
                        location={inEdit} 
                        onLocationUpdated={handleLocationUpdated}
                        onClose={ onLocationClose }
                    />
                }
                showModal={inEdit !== null}
                onCancel={onLocationClose} />
        </ProtectedPageWrapper>
    )
}


type LocationViewProps = {
    location:Location,
    allFacilityTypes:FacilityType[],
    onLocationDeleted: ()=>void,
    onLocationSelected: ()=>void,
    onLocationUpdated: ()=> Promise<void>
}

export function LocationView(props:LocationViewProps):JSX.Element
{
    const [errorMessage, setErrorMessage] = React.useState('');

    React.useEffect(() => {
        setErrorMessage('');
    }, [props.location]);

    async function handleClickDeleteLocation() {
        setErrorMessage('');
        if(props.location.facilities.length > 0){
            setErrorMessage('Kan ikke slette lokationen, da den er tilknyttet faciliteter');
        }
        else{
            const result = await deleteLocation(props.location.id);
            if(result.isSuccess){
                props.onLocationDeleted()
            }
        }
    }

    function renderCityAndPostalCode()
    {
        const wrapText = (text:string) => <FlexItem><Small>{text}</Small></FlexItem>
        
        if(props.location.postalCode && props.location.city)
            return wrapText(`${props.location.postalCode} ${props.location.city}`);
        if(props.location.postalCode)
            return wrapText(props.location.postalCode);
        if(props.location.city)
            return wrapText(props.location.city);
        
        return null;
    }
    
    return (
        <>
        {errorMessage &&
            <MessageCard variant='error'>
                Lokationen kan ikke slettes, da den er tilknyttet faciliteter
            </MessageCard>
        }

        <Card variant="light">
            <Flex flexDirection='column' gap={16}>
                <Flex flexDirection='column' gap={4}>
                    <Flex justifyContent='space-between'>
                        <Flex flexDirection='row'><H2>{props.location.name}</H2></Flex>
                        <AmorgMenuButton content={[
                            {
                                type:'leadingContent',
                                styleVariant:'default',
                                leadingContent:{ type:'icon', iconName:'interface_actions_edit'},
                                primaryContent:{ type:'text', text:'Rediger lokation'},
                                onClick: props.onLocationSelected
                            },
                            { type:'separator' },
                            {
                                type:'leadingContent',
                                styleVariant:'destructive',
                                leadingContent:{ type:'icon', iconName:'interface_actions_delete'},
                                primaryContent:{ type:'text', text:'Slet lokationen'},
                                onClick:handleClickDeleteLocation
                            }
                        ]} />
                    </Flex>

                    <Flex flexDirection='row'>
                        <div>
                            {props.location.address &&
                                <FlexItem><Small>{props.location.address}</Small></FlexItem>
                            }
                             {renderCityAndPostalCode()}
                        </div>
                    </Flex>
                </Flex>
                
                <FacilityList locationId={props.location.id} facilities={props.location.facilities} allFacilityTypes={props.allFacilityTypes} onUpdated={props.onLocationUpdated}/>
            </Flex>
        </Card>
    </>
    )
}